import { z } from "zod";
import { BaseResponseSchema } from "../../base/response/BaseResponseSchema";

const CustomerSchema = z.object({
    name: z.string(),
    surname: z.string(),
    phone: z.nullable(z.string()).optional(),
    mail: z.nullable(z.string()).optional(),
    address: z.nullable(z.string()).optional(),
    city: z.nullable(z.string()).optional(),
    zip: z.nullable(z.string()).optional(),
    country: z.nullable(z.string()).optional(),
})

const OccupancySchema = z.object({
    children: z.number(),
    adults: z.number(),
    ages: z.nullable(z.any()),
    infants: z.nullable(z.number()),
})

const MetaSchema = z.object({
    is_imported: z.nullable(z.boolean()).optional(),
})

const PropertySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    // Nullable: property deactivation nulls this server-side (DeactivatePropertyChannexJob).
    channex_id: z.string().uuid().nullable(),
    accommodation_type: z.number(),
    preferred_currency: z.string(),
    time_zone: z.string(),
    primary_email: z.string(),
    cc_emails: z.array(z.string()),
    accounting_email: z.nullable(z.string()),
    mobile_number_country_code: z.string().nullable(),
    mobile_number: z.string(),
    enable_cvv: z.number(),
    primary_phone_country_code: z.string().nullable(),
    primary_phone: z.string().nullable(),
    website: z.string().nullable(),
    country: z.string(),
    state: z.string().nullable(),
    city: z.string().nullable(),
    street_address: z.string(),
    postal_code: z.string(),
    prefix_booking_number: z.string().nullable(),
    hotel_tax_number: z.string().nullable(),
    latitude: z.string().nullable(),
    longitude: z.string().nullable(),
    description: z.string().nullable(),
    facebook_link: z.string().nullable(),
    instagram_link: z.string().nullable(),
    youtube_link: z.string().nullable(),
    tiktok_link: z.string().nullable(),
    logo: z.string().nullable(),
    cover_image: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    is_full_sync: z.boolean(),
    property_type: z.string(),
    settings: z.any().nullable(),
    logo_url: z.string().nullable(),
})

const BookingRateSchema = z.object({
    id: z.number(),
    booking_room_id: z.string(),
    rate: z.string(),
    date: z.string(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
})

const BookingRoomMetaSchema = z.object({
    mapping_id: z.nullable(z.string()).optional(),
    rate_plan_code: z.coerce.string(),
    room_type_code: z.nullable(z.coerce.string()).optional(),
    cancel_penalties: z.array(z.any()),
    days_breakdown: z.array(z.any()),
    booking_com_room_index: z.nullable(z.number()).optional(),
    meal_plan: z.nullable(z.string()).optional(),
    roomreservation_id: z.nullable(z.string()).optional(),
    policies: z.nullable(z.string()).optional(),
})

const BookingRoomSchema = z.object({
    id: z.string(),
    property_id: z.string(),
    booking_id: z.string(),
    customer_name: z.string(),
    customer_surname: z.nullable(z.string()),
    customer_email: z.string().nullable(),
    customer_phone: z.string().nullable(),
    customer_address: z.string().nullable(),
    customer_country: z.string().nullable(),
    customer_state: z.string().nullable(),
    customer_city: z.string().nullable(),
    customer_zip: z.string().nullable(),
    customer_date_of_birth: z.string().nullable(),
    customer_identity_number: z.string().nullable(),
    invoice_number: z.string(),
    invoice_date: z.string(),
    status: z.string(),
    mark: z.any().nullable(),
    payment_type: z.nullable(z.string()),
    room_type_id: z.string(),
    room_type: z.object({
        name: z.string(),
    }),
    room: z.object({
        room_number: z.string(),
    }),
    rate_plan: z.object({
        name: z.string(),
    }),
    room_id: z.string(),
    rate_plan_id: z.string(),
    rates: z.array(
        z.object({
            date: z.string(),
            rate: z.coerce.string(),
        }),
    ),
    total_rate: z.string(),
    checkin_date: z.string(),
    checkout_date: z.string(),
    adults: z.number(),
    children: z.number(),
    total_guests: z.number(),
    public_note: z.string().nullable(),
    private_note: z.string().nullable(),
    source: z.string(),
    channex_booking_room_id: z.string(),
    channex_revision_id: z.string(),
    channex_meta: BookingRoomMetaSchema,
    channex_taxes: z.array(z.any()),
    channex_services: z.array(z.any()),
    channex_amount: z.string(),
    channex_ota_commission: z.any().nullable(),
    channex_days: z.record(z.string()),
    channex_guests: z.any().nullable(),
    channex_occupancy: OccupancySchema,
    channex_rate_plan_id: z.string(),
    channex_room_type_id: z.string(),
    channex_checkin_date: z.string(),
    channex_checkout_date: z.string(),
    channex_is_cancelled: z.number(),
    channex_ota_unique_id: z.string().nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
    agent_id: z.string().nullable(),
    booking_rates: z.array(BookingRateSchema),
})

export const BookingVoucherSchema = z.object({
    id: z.string(),
    property_id: z.string(),
    booking_group_id: z.string(),
    source: z.string(),
    is_latest_revision: z.number(),
    channex_booking_group_id: z.string(),
    channex_revision_id: z.string(),
    channex_channel_id: z.string(),
    channex_property_id: z.string(),
    channex_status: z.string(),
    channex_services: z.array(z.any()),
    channex_currency: z.string(),
    channex_amount: z.string(),
    channex_inserted_at: z.string(),
    channex_ota_code: z.string(),
    channex_ota_name: z.string(),
    channex_system_id: z.string(),
    channex_arrival_date: z.string(),
    channex_arrival_hour: z.any().nullable(),
    channex_departure_date: z.string(),
    channex_customer: CustomerSchema,
    channex_deposits: z.array(z.any()),
    channex_notes: z.string().nullable(),
    channex_ota_commission: z.any().nullable(),
    channex_ota_reservation_code: z.string(),
    channex_payment_collect: z.any().nullable(),
    channex_unique_id: z.string(),
    channex_payment_type: z.any().nullable(),
    channex_occupancy: OccupancySchema,
    channex_guarantee: z.any().nullable(),
    channex_acknowledge_status: z.string(),
    channex_raw_message: z.string(),
    channex_meta: MetaSchema,
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
    property: PropertySchema,
    booking_rooms: z.array(BookingRoomSchema),
});

export const IndexBookingVoucherResponseSchema = BaseResponseSchema(BookingVoucherSchema);
export type IndexBookingVoucherResponse = z.infer<typeof IndexBookingVoucherResponseSchema>;
export type BookingVoucher = z.infer<typeof BookingVoucherSchema>;